import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';
import { DEMO_QUESTION_LIMIT } from '../demo.constants';
import { DemoAnswerState, DemoQuestionView, emptyAnswerState } from '../demo.models';
import { DemoService } from '../demo.service';

@Component({
  selector: 'app-demo-question',
  templateUrl: './demo-question.component.html',
  styleUrls: ['./demo-question.component.scss']
})
export class DemoQuestionComponent implements OnInit, OnDestroy {
  options = ['A', 'B', 'C', 'D', 'E'];
  isSpinner = false;
  questionIndex = 1;
  total = DEMO_QUESTION_LIMIT;
  question: DemoQuestionView;
  answer: DemoAnswerState;
  isFallback = false;
  private paramSub: Subscription | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private demoService: DemoService,
    private notification: NotificationService,
    private translate: TranslatePipe
  ) {
    this.question = {
      id: 0,
      questionText: '',
      image: '',
      options: ['', '', '', '', ''],
      sourceName: '',
      sourceLink: '',
      nextQuestion: null,
      previousQuestion: null
    };
    this.answer = emptyAnswerState();
  }

  ngOnInit(): void {
    this.paramSub = combineLatest([
      this.activatedRoute.paramMap,
      this.activatedRoute.queryParamMap
    ]).subscribe(([params, query]) => {
      this.demoService.applyQueryLanguage(query.get('lang'), query.get('language'));
      const raw = params.get('index');
      const index = raw ? +raw : 1;
      this.loadIndex(index);
    });
  }

  ngOnDestroy(): void {
    if (this.paramSub) {
      this.paramSub.unsubscribe();
    }
  }

  get checkAnswer(): boolean {
    return this.answer.checked;
  }

  get hasPrevious(): boolean {
    return this.questionIndex > 1;
  }

  get hasNext(): boolean {
    return this.questionIndex < this.total;
  }

  get progressPercent(): number {
    if (!this.total) {
      return 0;
    }
    return (this.questionIndex / this.total) * 100;
  }

  checkOption(id: number): void {
    this.answer.userCheck[id] = !this.answer.userCheck[id];
    if (this.answer.checked) {
      const cleared = emptyAnswerState();
      this.answer.checked = false;
      this.answer.isCorrect = null;
      this.answer.optionHint = cleared.optionHint;
      this.answer.optionIsCorrect = cleared.optionIsCorrect;
      this.demoService.resetCheck(this.question.id);
    }
    this.demoService.saveSelection(this.question.id, this.answer.userCheck);
  }

  async sendAnswer(): Promise<void> {
    try {
      const result = await this.demoService.checkAnswer(this.question.id, this.answer.userCheck);
      this.answer.checked = true;
      this.answer.isCorrect = result.isCorrect;
      this.answer.optionHint = result.optionHint;
      this.answer.optionIsCorrect = result.optionIsCorrect;
    } catch (err) {
      this.notification.sendNotification(this.translate.transform('demoUnavailable'), 'error');
    }
  }

  prevQuestion(): void {
    if (!this.hasPrevious) {
      return;
    }
    // next_prev_question follows the full bank, not this 10-question list.
    this.router.navigate(['/demo/question', this.questionIndex - 1], { queryParamsHandling: 'preserve' });
  }

  nextQuestion(): void {
    if (!this.hasNext) {
      return;
    }
    this.router.navigate(['/demo/question', this.questionIndex + 1], { queryParamsHandling: 'preserve' });
  }

  finishDemo(): void {
    this.demoService.markCompleted();
    this.router.navigate(['/demo/complete'], { queryParamsHandling: 'preserve' });
  }

  optionCssClass(id: number): string {
    if (this.answer.checked) {
      if (this.answer.optionIsCorrect[id] && this.answer.userCheck[id]) {
        return 'option-letter-circle option-correct';
      }
      if (!this.answer.optionIsCorrect[id] && this.answer.userCheck[id]) {
        return 'option-letter-circle option-wrong';
      }
      if (this.answer.optionIsCorrect[id] && !this.answer.userCheck[id]) {
        return 'option-letter-circle option-no-check-correct';
      }
      return 'option-letter-circle option-no-check-wrong';
    }
    if (this.answer.userCheck[id]) {
      return 'option-letter-circle option-checked';
    }
    return 'option-letter-circle';
  }

  explanationCssClass(id: number): string {
    if (this.answer.optionIsCorrect[id] && this.answer.userCheck[id]) {
      return 'explanation option-correct-explanation';
    }
    if (!this.answer.optionIsCorrect[id] && this.answer.userCheck[id]) {
      return 'explanation option-wrong-explanation';
    }
    return 'explanation';
  }

  optionTextCssClass(id: number): string {
    if (this.answer.optionIsCorrect[id] && this.answer.userCheck[id]) {
      return 'answer check-question-text-correct';
    }
    if (!this.answer.optionIsCorrect[id] && this.answer.userCheck[id]) {
      return 'answer check-question-text-wrong';
    }
    return 'answer';
  }

  private async loadIndex(index: number): Promise<void> {
    this.isSpinner = true;
    try {
      if (!this.demoService.hasSession()) {
        await this.demoService.startSession();
      }
      this.total = this.demoService.getTotal();
      this.isFallback = this.demoService.getSource() === 'fallback';

      if (!index || index < 1) {
        this.router.navigate(['/demo/question', 1], { queryParamsHandling: 'preserve' });
        return;
      }
      if (index > this.total) {
        this.demoService.markCompleted();
        this.router.navigate(['/demo/complete'], { queryParamsHandling: 'preserve' });
        return;
      }

      this.questionIndex = index;
      this.question = await this.demoService.getQuestion(index);
      this.answer = this.cloneAnswer(this.demoService.getAnswer(this.question.id));
    } catch (err) {
      this.notification.sendNotification(this.translate.transform('demoUnavailable'), 'error');
      this.router.navigate(['/demo']);
    }
    this.isSpinner = false;
  }

  private cloneAnswer(state: DemoAnswerState): DemoAnswerState {
    return {
      userCheck: state.userCheck.slice(),
      checked: state.checked,
      isCorrect: state.isCorrect,
      optionHint: state.optionHint.slice(),
      optionIsCorrect: state.optionIsCorrect.slice()
    };
  }
}
